/**
 * CR-8624: the columns copied from the customer's out-of-stock sheet.
 *
 * Committed Next 4 / 3 / 2 Weeks and Next Week count the open SAL commitments
 * due by then (overdue ones included). Total Shortage and Shortage Next N Weeks
 * take those off In Stock — without In Transit, which is what sets them apart
 * from Current Shortage, so each header says so on hover. A shortage below 0 is
 * shaded red, as the sheet shades it.
 */
import type { User } from '../../../lib/api';

/** The shortage columns, shaded red below 0. */
export const SHORTAGE_COLUMNS: readonly string[] = [
  'total_shortage',
  'shortage_next_4_weeks',
  'shortage_next_3_weeks',
  'shortage_next_2_weeks',
  'shortage_next_week',
];

/** Cell classes for a shortage below 0 — the pink the New Call Off grid uses. */
export const NEGATIVE_SHORTAGE_CLASS = 'bg-red-50 text-red-700 font-medium';

const NO_TRANSIT = 'Excludes In Transit, unlike Current Shortage.';
const committedBy = (days: number) =>
  `Open SAL committed due on or before today + ${days} days, overdue orders included.`;

/** Header hover text: what the column counts. */
export const COLUMN_HINTS: Readonly<Record<string, string>> = {
  committed_next_4_weeks: committedBy(28),
  committed_next_3_weeks: committedBy(21),
  committed_next_2_weeks: committedBy(14),
  committed_next_week: committedBy(7),
  total_shortage: `In Stock (Stock Status) − All SAL Committed. ${NO_TRANSIT}`,
  shortage_next_4_weeks: `In Stock (Stock Status) − Committed Next 4 Weeks. ${NO_TRANSIT}`,
  shortage_next_3_weeks: `In Stock (Stock Status) − Committed Next 3 Weeks. ${NO_TRANSIT}`,
  shortage_next_2_weeks: `In Stock (Stock Status) − Committed Next 2 Weeks. ${NO_TRANSIT}`,
  shortage_next_week: `In Stock (Stock Status) − Committed Next Week. ${NO_TRANSIT}`,
  remarks: 'Notes on this part from this supplier. Customer admins can edit; suppliers never see them.',
};

/**
 * Whether a cell is a shortage below 0.
 *
 * @param columnId - the cell's column
 * @param value - the cell's value as shown
 * @returns true for a negative figure in one of `SHORTAGE_COLUMNS`
 */
export function isNegativeShortage(columnId: string, value: string): boolean {
  if (!SHORTAGE_COLUMNS.includes(columnId)) return false;
  const num = parseFloat(value);
  return !isNaN(num) && num < 0;
}

/**
 * Whether this user may type or clear a remark. Mirrors the backend rule in
 * `stock_planning_remarks.can_edit_remark`: Helix, or an admin on the customer
 * side. Every other customer user reads the remarks only.
 *
 * @param user - the signed-in user
 * @returns true for a customer admin or a Helix user
 */
export function canEditRemark(user: User | null | undefined): boolean {
  if (!user) return false;
  if (user.company_type === 'helix') return true;
  const isCustomer = user.company_type === 'customer' || user.platform === 'CUSTOMER';
  return isCustomer && Boolean(user.is_company_admin);
}

/**
 * Hover text for a remark: who last changed it, and when.
 *
 * @example remarkTitle('Ada Admin', '2026-10-17T09:05:00Z', formatDateTime)
 *   // → 'Last changed by Ada Admin, 17/10/2026 10:05'
 * @param byName - who last changed it
 * @param at - when (ISO string)
 * @param formatDateTime - the user's date-time formatter
 * @returns the hover text, or '' when neither is known
 */
export function remarkTitle(
  byName: string | null | undefined,
  at: string | null | undefined,
  formatDateTime: (date: string) => string,
): string {
  const who = byName ? ` by ${byName}` : '';
  const when = at ? `, ${formatDateTime(at)}` : '';
  return who || when ? `Last changed${who}${when}` : '';
}
